import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    GetRequest,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import {
    Flyer,
    FlyerSearchFilterOptions,
    FlyerUpdateRequestOptions
} from "@shopliftr/common-js/admin";
import {
    IQueryInternalApiClientService,
    IUpdateInternalApiClientService
} from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";

/**
 * API client for the v2 flyer endpoints (`/v2/flyer`).
 *
 * **Service Type:** API Client Service
 *
 * **Provided:** Singleton in `root`
 *
 * A child flyer is a node in its root flyer's `children` tree, not a document of its own, so every read returns a
 * root flyer and every update sends the whole root, tree included, under the root's id and `esVersion`, with a
 * {@link FlyerUpdateRequestOptions} naming the task the edit is made under. Flyers are created and pruned by the batch
 * engine, so there is no create or delete. Admin code edits flyers through `FlyerUpdateWorkflowService`, which also
 * handles version conflicts; call `update$` directly only for a save that needs no conflict handling.
 */
@Injectable({
    providedIn: "root"
})
export class FlyerApiClientService implements
    IUpdateInternalApiClientService<Flyer>,
    IQueryInternalApiClientService<Flyer> {

    readonly basePath = "flyer";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve several root flyers by id in one bulk request.
     *
     * @param ids - Root flyer ids
     * @returns Observable emitting a BulkResponse with one get response per id
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Flyer>(id, Flyer.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a root flyer, tree included and sorted by name, with its current `esVersion`.
     *
     * @param id - Root flyer id
     * @returns Observable emitting the root flyer
     */
    get$(id: string): Observable<Flyer> {

        const getRequest: GetRequest<Flyer> = RequestUtil.createGetRequest(
            id, Flyer.modelName, ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(map((response) => response.payload as Flyer));
    }

    /**
     * Retrieve every root flyer matching the filter options, appending all result pages.
     *
     * @param filterOptions - Search criteria (e.g. `ids` for a batch load)
     * @param sort - Sort criteria
     * @returns Observable emitting all matching root flyers
     */
    getAll$(filterOptions: FlyerSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Flyer>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Flyer.modelName,
            0,
            100,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Search root flyers with pagination.
     *
     * @param filterOptions - Search criteria
     * @param page - Zero-based page index
     * @param pageSize - Number of flyers per page
     * @param sort - Sort criteria
     * @returns Observable emitting the page of results and the total
     */
    search$(
        filterOptions: FlyerSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Flyer>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Flyer.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Save a root flyer: `PUT /v2/flyer/{id}` with the whole root, tree included.
     *
     * @param id - Root flyer id
     * @param version - The root's `esVersion` as loaded; a newer server version fails with a `RecordModifiedException`
     * @param fields - The whole root flyer with the edit applied to the right node
     * @param removeFields - Fields to clear, in the v2 `removeFields` convention
     * @param options - Required: the task the edit is made under
     * @returns Observable emitting the saved, sorted root with its new `esVersion`
     * @throws InvalidParametersException when `options` or its `taskId` is missing
     */
    update$(
        id: string,
        version: number,
        fields: Flyer,
        removeFields?: Array<string>,
        options?: FlyerUpdateRequestOptions
    ): Observable<Flyer> {

        if (!options?.taskId) {
            throw new InvalidParametersException("Could not update flyer: options with a taskId are required, but were not provided");
        }

        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Flyer, FlyerUpdateRequestOptions> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields, options
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Save the changes between two copies of a root flyer.
     *
     * @remarks It cannot express flyer clears: the server ignores an omitted child `flyerUrl`, and any `removeFields`
     * entry other than `flyerUrl` is a 400. To clear a flyer use `update$` (or `FlyerUpdateWorkflowService`).
     *
     * @param original - The root as loaded (its `esVersion` is sent)
     * @param updated - The root with the edit applied
     * @param options - Required: the task the edit is made under
     * @returns Observable emitting the saved root
     * @throws InvalidParametersException when `options` or its `taskId` is missing
     */
    updateWithChanges$(original: Flyer, updated: Flyer, options?: FlyerUpdateRequestOptions): Observable<Flyer> {

        if (!options?.taskId) {
            throw new InvalidParametersException("Could not update flyer: options with a taskId are required, but were not provided");
        }

        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath, options);
    }

    /**
     * Save several root flyers in one bulk request. Two updates of the same root in one call conflict on `version`.
     *
     * @param requests - Update requests to send
     * @returns Observable emitting a BulkResponse with one result per request
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
